import { api } from "../../api"
import { setAlertFunc } from "../../components/AlertToaster/AlertSlice"
import { AccountObj, HistoryResponse, IResponse, tagsDetail } from "../Common/commonTypes"
import { IFile, UserInfo } from "../Common/commonTypes"

export type ContactObj = {
    id: number
    account_id: number
    contact_type: string
    email: string
    name: string
    phone: number | string
    is_primary: 0 | 1
}
type Location = {
    city: string;
    city_id: number;
    state: string;
    state_id: number;
};
export type Zones = Record<string, Location[]>;

export type BillingAddressObj = {
    id: number
    account_id: number
    address: string
    city: string
    city_id: number
    gst_certificate_url: IFile
    gstin: string
    is_active: 0 | 1
    is_gst_certificate: boolean
    is_primary: 0 | 1
    pincode: number
    state: string
    state_id: number
    gst_certificate_path: string
    country_id: number
}

export type AccountsTableData = {
    account_contacts: ContactObj[]
    account_name: string
    added_by: Partial<UserInfo>
    billing_addresses: BillingAddressObj[]
    city_id: number
    created_at: string
    linked_projects: Record<string, any>
    account_type: string
    crm_account_id: string
    gstin: string
    id: number
    tags?: {
        others: tagsDetail[],
        primary: tagsDetail[]
    }
    departments?: string[]
    services?: { id: number; name: string }[]
    account_display_id: string
    is_active: 1 | 0
    parent_account_name: string
    pincode: number
    state_id: number
    updated_at: string
    legal_business_name: string
    operational_location: string
    department: string
    ref_id?: string
    reference_id:string,
    source: string
    zones: Zones
    pan?: string
    msme_reg_number?: string
    msme_reg_type?: string
    gst_treatment?: string
    msme_reg?: 1 | 0
}

export type BankDetailsResponseObj = {
    account_holder_name: string
    account_id: number
    account_number: string
    bank_name: string
    created_at: string
    id: number
    ifsc_code: string
    is_active: 1 | 0
    updated_at: string
}

const MODULE_URL = "accounts"

const accountsService = api.injectEndpoints({
    endpoints: build => ({
        getAccounts: build.query<
            { data: AccountsTableData[] },
            {
                added_by?: string
                pa_ids?: string
                parent_account_id?: string
                search_key?: string
                status?: number | ""
                page?: number
                tag_ids?: any
                per_page?: number
                account_type?: "all" | "Client" | "Vendor"
                departments?: string
                services?: string
                source?: string
                zone?: string
                state_id?: string
                city_id?: string
                country_id?: number | ""
            }
        >({
            query: params => ({
                url: `${MODULE_URL}/list_accounts`,
                params: {
                    ...params,
                },
            }),
            providesTags: ["Accounts"],
        }),
        getLinkedAccount: build.query<IResponse<AccountObj[]>, void>({
            query: () => ({
                url: `${MODULE_URL}/get_accounts_for_projects`,
            }),
            providesTags: ["Accounts"],
        }),
        getAccountServices: build.query<any, void>({
            query: () => ({
                url: `${MODULE_URL}/get_services`,
            }),
            transformResponse: (response: IResponse<any[]>) => response.data,
        }),
        getAccountDepartment: build.query<any, void>({
            query: () => ({
                url: `${MODULE_URL}/get_departments`,
            }),
            transformResponse: (response: IResponse<any[]>) => response.data,
        }),
        getFilterParentAccount: build.query<IResponse<AccountObj[]>, void>({
            query: () => ({
                url: `${MODULE_URL}/get_parent_accounts`,
                params: {
                    search_key: "",
                },
            }),
            providesTags: ["Accounts"],
        }),
        getFormParentAccount: build.query<any[], void>({
            query: () => ({
                url: `${MODULE_URL}/get_accounts_compact`,
                params: {
                    search_key: "",
                },
            }),
            providesTags: ["Accounts"],
        }),
        getAccountTicketHistory: build.query<
            { data: HistoryResponse[]; totalPages: number },
            { account_id: string; page: number; per_page: number }
        >({
            query: params => ({
                url: `${MODULE_URL}/account_history`,
                params: params,
            }),
            providesTags: (result, error, arg) => [{ type: "Accounts", id: arg.account_id }],
        }),

        editAccountStatus: build.mutation<any, { account_id: number; status: 1 | 0 }>({
            query: body => ({
                url: `${MODULE_URL}/edit_account_status`,
                method: "PUT",
                body,
            }),
            invalidatesTags: ["Accounts"],
        }),
        getAccountsAddedBy: build.query<{ added_by: number; name: string }[], void>({
            query: () => ({
                url: `${MODULE_URL}/get_added_by`,
            }),
        }),
        getBankDetails: build.query<BankDetailsResponseObj[], { account_id: number }>({
            query: params => ({
                url: `${MODULE_URL}/bank_details`,
                params: params,
            }),
            providesTags: ["Accounts"],
        }),
        addBankDetails: build.mutation<any, { account_id: number; bank_details: { bank_name: string; account_number: string; ifsc_code: string; account_holder_name: string, is_active: 1 | 0 }[] }>({
            query: body => ({
                url: `${MODULE_URL}/bank_details`,
                method: "PUT",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: "Bank Details Updated!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Accounts"],
        }),
        exportAccounts: build.query<
            Blob,
            {
                added_by: string
                pa_ids: string
                search_key: string
                status: number | ""
                account_type: string
            }
        >({
            query: params => ({
                url: `${MODULE_URL}/export`,
                params: params,
                responseHandler: (response: any) => response.blob(),
            }),
        }),
        getAccountMetrics: build.query<
            {
                all_count: number
                client_count: number
                vendor_count: number
                totalPages: number
            },
            {
                added_by?: string
                pa_ids?: string
                account_type?: "all" | "Client" | "Vendor"
                tag_ids?: any
                search_key?: string
                per_page?: number
                status?: number | ""
                departments?: string
                services?: string
                source?: string
                zone?: string
                state_id?: string
                city_id?: string
                country_id?: number | ""
            }
        >({
            query: params => ({
                url: `${MODULE_URL}/account_metrics`,
                params: params,
            }),
            providesTags: ["Accounts"],
        }),
        // Mutations
        addAccount: build.mutation<
            any,
            {
                account_name: string
                legal_business_name: string
                billing_addresses: any
                account_contacts: any
                parent_account_id?: number
                id?: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/account_management`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: "Ticket Created!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.error?.data?.error }))
                }
            },

            invalidatesTags: ["Accounts"],
        }),
        editAccount: build.mutation<
            any,
            {
                account_name: string
                legal_business_name: string
                billing_addresses: any
                account_contacts: any
                parent_account_id?: number
                id: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/account_management`,
                method: "PUT",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: ["Accounts"],
        }),
        editBankDetails: build.mutation<any, { account_id: number; bank_details: { bank_name: string; account_number: string; ifsc_code: string; account_holder_name: string, is_active?: 1 | 0, id?: number }[] }>({
            query: body => ({
                url: `${MODULE_URL}/bank_details`,
                method: "PUT",
                body,
            }),
            invalidatesTags: ["Accounts"],
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: "Bank Details Updated!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
        }),
    }),
})

export const {
    useGetAccountsQuery,
    useLazyGetAccountsQuery,
    useGetLinkedAccountQuery,
    useGetAccountServicesQuery,
    useGetAccountDepartmentQuery,
    useGetFilterParentAccountQuery,
    useGetFormParentAccountQuery,
    useGetAccountsAddedByQuery,
    useLazyGetAccountTicketHistoryQuery,
    useLazyExportAccountsQuery,
    useEditAccountStatusMutation,
    useAddAccountMutation,
    useEditAccountMutation,
    useGetAccountMetricsQuery,
    useLazyGetAccountMetricsQuery,
    useGetBankDetailsQuery,
    useAddBankDetailsMutation,
    useEditBankDetailsMutation,
} = accountsService
